/**
 * ラリー詳細のリストビュー
 *
 * 都道府県ごとのアコーディオン。行タップでチェックを切り替える。
 * 近い順のときだけはグループをまたいで並べたいのでフラットな 1 本のリストにする。
 */

import { CheckCircle2, ChevronDown, Circle, EllipsisVertical } from 'lucide-react'
import { useState } from 'react'

import type { StampRallyGroup, StampRallyItem } from '@/api/types/stamp-rally'
import { matchesStampFilter, type StampFilter } from '@/components/shared/map/stamp-filter'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import type { Coords } from '@/hooks/useCurrentPosition'
import { formatDistanceKm } from '@/lib/activity-format-utils'
import { calculateDistance } from '@/lib/geo-utils'
import { cn } from '@/lib/utils'

export type ItemSort = 'official' | 'distance'

/** 座標のないスポットは距離を出せないので null を返し、並び順では末尾に回す */
const distanceOf = (item: StampRallyItem, origin: Coords): number | null => {
  if (item.latitude === null || item.longitude === null) return null
  return calculateDistance(origin.latitude, origin.longitude, item.latitude, item.longitude)
}

type Props = {
  groups: StampRallyGroup[]
  filter: StampFilter
  sort: ItemSort
  origin: Coords | null
  color: string
  onToggle: (item: StampRallyItem) => void
  onOpenItem: (item: StampRallyItem) => void
}

const ItemRow = ({
  item,
  color,
  distanceKm,
  onToggle,
  onOpenItem,
}: {
  item: StampRallyItem
  color: string
  distanceKm?: number | null
  onToggle: (item: StampRallyItem) => void
  onOpenItem: (item: StampRallyItem) => void
}) => {
  const stamped = Boolean(item.stamped_at)

  return (
    <div className="flex items-center border-b border-border/50 last:border-b-0">
      <button onClick={() => onToggle(item)} className="flex min-w-0 flex-1 items-center gap-3 px-3! py-3! text-left">
        {stamped ? (
          <CheckCircle2 size={20} className="shrink-0" style={{ color }} />
        ) : (
          <Circle size={20} className="shrink-0 text-muted-foreground/50" />
        )}
        <div className="min-w-0 flex-1">
          <p className={cn('truncate text-sm', !stamped && 'text-muted-foreground')}>{item.name}</p>
          {item.note && <p className="truncate text-xs text-muted-foreground">{item.note}</p>}
        </div>
        {distanceKm != null && (
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{formatDistanceKm(distanceKm)}</span>
        )}
      </button>
      <button
        onClick={() => onOpenItem(item)}
        aria-label={`${item.name} の詳細`}
        className="shrink-0 px-2! py-3! text-muted-foreground"
      >
        <EllipsisVertical size={18} />
      </button>
    </div>
  )
}

export const RallyListView = ({ groups, filter, sort, origin, color, onToggle, onOpenItem }: Props) => {
  const [closed, setClosed] = useState<Set<string>>(new Set())

  const visible = groups
    .map((group) => ({ ...group, items: group.items.filter((item) => matchesStampFilter(item.stamped_at, filter)) }))
    .filter((group) => group.items.length > 0)

  if (visible.length === 0) {
    return (
      <p className="py-16 text-center text-sm text-muted-foreground">
        {filter === 'all' && groups.length === 0 ? 'まだスポットがありません' : '該当するスポットがありません'}
      </p>
    )
  }

  if (sort === 'distance' && origin) {
    const rows = visible
      .flatMap((group) => group.items)
      .map((item) => ({ item, distanceKm: distanceOf(item, origin) }))
      .sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity))

    return (
      <div className="overflow-hidden rounded-xl border bg-card">
        {rows.map(({ item, distanceKm }) => (
          <ItemRow
            key={item.id}
            item={item}
            color={color}
            distanceKm={distanceKm}
            onToggle={onToggle}
            onOpenItem={onOpenItem}
          />
        ))}
      </div>
    )
  }

  // グループ名が 1 つ（未設定のみ）ならアコーディオンにせず素のリストにする
  const isFlat = visible.length === 1 && visible[0].name === null
  if (isFlat) {
    return (
      <div className="overflow-hidden rounded-xl border bg-card">
        {visible[0].items.map((item) => (
          <ItemRow key={item.id} item={item} color={color} onToggle={onToggle} onOpenItem={onOpenItem} />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {visible.map((group) => {
        const key = group.name ?? ''
        const stamped = group.items.filter((item) => item.stamped_at).length
        return (
          <Collapsible
            key={key}
            open={!closed.has(key)}
            onOpenChange={(open) =>
              setClosed((prev) => {
                const next = new Set(prev)
                if (open) next.delete(key)
                else next.add(key)
                return next
              })
            }
            className="overflow-hidden rounded-xl border bg-card"
          >
            <CollapsibleTrigger className="flex w-full items-center gap-2 px-3! py-2.5! text-left">
              <ChevronDown size={16} className={cn('shrink-0 transition-transform', closed.has(key) && '-rotate-90')} />
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{group.name ?? 'その他'}</span>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {stamped} / {group.items.length}
              </span>
            </CollapsibleTrigger>
            <CollapsibleContent className="border-t border-border/50">
              {group.items.map((item) => (
                <ItemRow key={item.id} item={item} color={color} onToggle={onToggle} onOpenItem={onOpenItem} />
              ))}
            </CollapsibleContent>
          </Collapsible>
        )
      })}
    </div>
  )
}
